import React from 'react';
import { CalendarEvent } from '../../../types/calendar';
import { formatTimeRange } from '../../../utils/calendarUtils';
import { useAppDispatch } from '../../../store/hooks';
import { toggleEventCompletion } from '../../../store/slices/calendarSlice';

interface EventCardProps {
  event: CalendarEvent;
  onClick: (event: CalendarEvent) => void;
  compact?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick, compact = false }) => {
  const dispatch = useAppDispatch();
  const backgroundColor = event.color || '#2196F3';
  const isDeliveryEvent = event.title.startsWith('Delivery:');
  const isCompleted = event.completed || false;

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    dispatch(toggleEventCompletion(event.id));
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(event);
  };

  if (compact) {
    return (
      <div
        style={{
          ...styles.compactCard,
          backgroundColor,
          ...(isCompleted && styles.completedCard)
        }}
        onClick={handleClick}
        title={event.title}
      >
        {isDeliveryEvent && (
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleCheckboxChange}
            onClick={e => e.stopPropagation()}
            style={styles.checkbox}
          />
        )}
        <span
          style={{
            ...styles.compactTitle,
            ...(isCompleted && styles.completedText)
          }}
        >
          {event.allDay ? '• ' : ''}
          {event.title}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.card,
        borderLeft: `4px solid ${backgroundColor}`,
        ...(isCompleted && styles.completedCard)
      }}
      onClick={handleClick}
    >
      <div style={styles.cardHeader}>
        {isDeliveryEvent && (
          <input
            type="checkbox"
            checked={isCompleted}
            onChange={handleCheckboxChange}
            onClick={e => e.stopPropagation()}
            style={styles.checkbox}
          />
        )}
        <h4
          style={{
            ...styles.title,
            ...(isCompleted && styles.completedText)
          }}
        >
          {event.title}
        </h4>
        {event.allDay ? (
          <span style={styles.allDayBadge}>All Day</span>
        ) : (
          event.startTime &&
          event.endTime && <span style={styles.time}>{formatTimeRange(event.startTime, event.endTime)}</span>
        )}
      </div>
      {event.description && (
        <p
          style={{
            ...styles.description,
            ...(isCompleted && styles.completedText)
          }}
        >
          {event.description}
        </p>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  card: {
    backgroundColor: 'white',
    padding: '12px',
    marginBottom: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.2s'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
    gap: '8px'
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    flex: 1
  },
  time: {
    fontSize: '12px',
    color: '#666',
    whiteSpace: 'nowrap',
    marginLeft: '8px'
  },
  allDayBadge: {
    fontSize: '11px',
    color: '#666',
    backgroundColor: '#f0f0f0',
    padding: '2px 8px',
    borderRadius: '12px',
    whiteSpace: 'nowrap',
    marginLeft: '8px'
  },
  description: {
    margin: 0,
    fontSize: '12px',
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  compactCard: {
    padding: '4px 8px',
    marginBottom: '2px',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'white',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  compactTitle: {
    fontWeight: '500'
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    flexShrink: 0,
    accentColor: '#4CAF50'
  },
  completedCard: {
    opacity: 0.5
  },
  completedText: {
    color: '#999'
  }
};

export default EventCard;
